import * as firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/functions';
import { TRAINERS, GYMS } from 'utils/constants/collectionNames';
import {
  AUTH_ERRORS,
  UNVERIFIED_EMAIL,
  RESET_PASSWORD_ERROR
} from 'utils/constants/errors';
import {
  LOGIN_SUCCEEDED,
  LOGIN_ERROR,
  SIGNUP_ERROR,
  SIGNUP_SUCCEEDED,
  CLEAR_AUTH,
  LOGOUT_SUCCEEDED,
  LOADING_STATE,
  UNVERIFIED_EMAIL_ERROR,
  RESET_PASSWORD_SUCCEEDED,
  RESET_PASSWORD_FAILED,
  GET_GYMS_SUCCEEDED,
  GET_GYMS_ERROR
} from './types';

// Signing in with Firebase
export const signin = (email, password) => async dispatch => {
  try {
    dispatch({ type: LOADING_STATE });
    return firebase
      .auth()
      .setPersistence(firebase.auth.Auth.Persistence.LOCAL)
      .then(function() {
        return firebase
          .auth()
          .signInWithEmailAndPassword(email, password)
          .then(async () => {
            let firestore = firebase.firestore();
            let user = firebase.auth().currentUser;
            let isEmailVerified = user.emailVerified;
            if (!isEmailVerified) {
              return logoutNotVerifiedEmails(user, isEmailVerified, dispatch);
            }
            await firestore
              .collection(TRAINERS)
              .doc(user.uid)
              .get()
              .then(doc => {
                if (doc.exists) {
                  if (doc.data() && !doc.data().first_login) {
                    return updataTrainerFirstLoginValue(doc.id, dispatch);
                  } else {
                    dispatch({
                      type: LOGIN_SUCCEEDED,
                      payload: { firstLogIn: false }
                    });
                  }
                } else {
                  return logoutNotVerifiedEmails(
                    null,
                    isEmailVerified,
                    dispatch
                  );
                }
              })
              .catch(error => {
                console.log(error);
                return logoutNotVerifiedEmails(null, isEmailVerified, dispatch);
              });
          })
          .catch(err => {
            console.log(err);
            dispatch({
              type: AUTH_ERRORS[err.code].type,
              payload: AUTH_ERRORS[err.code].message
            });
          });
      })
      .catch(function(error) {
        // Handle Errors here.
        dispatch({ type: LOGIN_ERROR, payload: error.message });
      });
  } catch (err) {
    dispatch({ type: LOGIN_ERROR, payload: `error: ${err}` });
  }
};

export const logoutNotVerifiedEmails = (user, isEmailVerified, dispatch) => {
  return firebase
    .auth()
    .signOut()
    .then(function() {
      if (user && !isEmailVerified) {
        dispatch({
          type: UNVERIFIED_EMAIL_ERROR,
          payload: UNVERIFIED_EMAIL
        });
      } else {
        dispatch({
          type: AUTH_ERRORS['auth/user-not-found'].type,
          payload: AUTH_ERRORS['auth/user-not-found'].message
        });
      }
    })
    .catch(function(error) {
      console.log('logout', error);
    });
};

export const updataTrainerFirstLoginValue = (trainerId, dispatch) => {
  let firestore = firebase.firestore();
  var docRef = firestore.collection(TRAINERS).doc(trainerId);
  return docRef
    .set({ first_login: true }, { merge: true })
    .then(function() {
      dispatch({
        type: LOGIN_SUCCEEDED,
        payload: { firstLogIn: true }
      });
    })
    .catch(function(error) {
      console.log(`update trainer error: ${error}`);
    });
};

// Signup with Firebase
export const signup = (
  first_name,
  last_name,
  email,
  password,
  gym_id
) => async dispatch => {
  try {
    dispatch({ type: LOADING_STATE });
    const user = {
      email,
      password,
      first_name,
      last_name,
      gym_id
    };

    const registerTrainer = firebase
      .functions()
      .httpsCallable('authTrainerRegister');

    return registerTrainer(user)
      .then(async () => {
        sendVerificationEmail(email, password, dispatch);
      })
      .catch(err => {
        dispatch({
          type: SIGNUP_ERROR,
          payload: err
        });
      });
  } catch (err) {
    console.log(err);
    dispatch({ type: SIGNUP_ERROR, payload: 'Invalid signup credentials' });
  }
};

export const resetPassword = email => async dispatch => {
  try {
    dispatch({ type: LOADING_STATE });
    const resetTrainerPassword = firebase
      .functions()
      .httpsCallable('authClientResetPassword');

    return resetTrainerPassword({
      email: email,
      type: 'trainer'
    })
      .then(async Response => {
        dispatch({
          type: RESET_PASSWORD_SUCCEEDED,
          payload: {
            resetPasswordEmailSent: Response.data.sent,
            resetPasswordError: !Response.data.sent && RESET_PASSWORD_ERROR
          }
        });
      })
      .catch(error => {
        dispatch({
          type: RESET_PASSWORD_FAILED,
          payload: {
            resetPasswordError: error.message
          }
        });
      });
  } catch (error) {
    console.error(error);
    dispatch({
      type: RESET_PASSWORD_FAILED,
      payload: {
        resetPasswordError: RESET_PASSWORD_ERROR
      }
    });
  }
};

export const clearAuthState = () => async dispatch => {
  dispatch({
    type: CLEAR_AUTH
  });
};

export const logout = () => async dispatch => {
  firebase
    .auth()
    .signOut()
    .then(function() {
      dispatch({ LOGOUT_SUCCEEDED });
    })
    .catch(function(error) {
      console.log(error);
    });
};

export const resendVerificationEmail = (email, password) => async dispatch => {
  sendVerificationEmail(email, password, dispatch);
};

export const sendVerificationEmail = (email, password, dispatch) => {
  dispatch({
    type: LOADING_STATE
  });

  var actionCodeSettings = {
    url: `${process.env.REACT_APP_DYNAMIC_LINK_DOMAIN}trainer_login`,
    handleCodeInApp: false
  };

  return firebase
    .auth()
    .signInWithEmailAndPassword(email, password)
    .then(async () => {
      var user = firebase.auth().currentUser;
      user
        .sendEmailVerification(actionCodeSettings)
        .then(async () => {
          // Email sent.
          await firebase.auth().signOut();
          dispatch({
            type: SIGNUP_SUCCEEDED
          });
        })
        .catch(async error => {
          // An error happened.
          await firebase.auth().signOut();
          dispatch({
            type: SIGNUP_ERROR,
            payload: error
          });
        });
    })
    .catch(err => {
      console.log(`error: ${err}`);
      dispatch({
        type: AUTH_ERRORS[err.code].type,
        payload: AUTH_ERRORS[err.code].message
      });
    });
};

// get gym list from Firebase
export const getGyms = () => async dispatch => {
  firebase
    .firestore()
    .collection(GYMS)
    .get()
    .then(snapshot => {
      const gyms = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      dispatch({
        type: GET_GYMS_SUCCEEDED,
        payload: gyms
      });
    })
    .catch(err => {
      console.log(err);
      dispatch({
        type: GET_GYMS_ERROR,
        payload: `error: ${err}`
      });
    });
};
